import { Component, OnInit, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  RunTransition,
  RunTransitionWithDialog,
  WorkflowService,
} from 'src/app/shared/services/workflow/workflow.service';
import { PayeePopupComponent } from '../../payee-popup/payee-popup.component';
import { PayeeButtonComponent } from '../../payee-buttons/payee-button/payee-button.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { TranslateModule } from '@ngx-translate/core';
import {
  CloseCaseConfirmComponent,
  CloseCaseConfirmDialogData,
  CloseCaseConfirmResult,
} from './close-case-confirm.component';

type DialogData = {
  transition_ids: {
    close: string;
    full_payment: string;
  };
  claim_uuids: string[];
  amount: number;
};

@Component({
  selector: 'app-close-case',
  templateUrl: './close-case.component.html',
  styleUrls: ['./close-case.component.scss'],
  imports: [PayeePopupComponent, PayeeButtonComponent, PayeeIconComponent, TranslateModule],
})
export class CloseCaseComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<CloseCaseComponent>>(MatDialogRef);
  private dialog = inject(MatDialog);
  private data = inject<RunTransitionWithDialog>(MAT_DIALOG_DATA);
  private workflowService = inject(WorkflowService);

  loading = false;
  dialogData?: DialogData;

  async ngOnInit() {
    this.dialogData = (await this.workflowService.getDialogData({
      caseId: this.data.caseId,
      transitionId: this.data.transitionId,
    })) as DialogData;
  }

  readonly cancel = () => {
    this.dialogRef.close();
  };

  readonly submitPaid = () => this.confirmThenSubmit('paid');

  readonly submitOther = () => this.confirmThenSubmit('cancel');

  private readonly confirmThenSubmit = async (type: 'paid' | 'cancel') => {
    const confirmed = await firstValueFrom(
      this.dialog
        .open<CloseCaseConfirmComponent, CloseCaseConfirmDialogData, CloseCaseConfirmResult>(
          CloseCaseConfirmComponent,
          { data: { showCloseReason: type === 'cancel' } },
        )
        .afterClosed(),
    );
    if (confirmed === undefined) {
      return;
    }

    await this.submit(type, confirmed.message);
  };

  readonly submit = async (type: 'paid' | 'cancel', message?: string) => {
    if (!this.dialogData) {
      return;
    }

    const closeValue: RunTransition = {
      caseId: this.data.caseId,
      transitionId:
        type === 'cancel'
          ? this.dialogData.transition_ids.close
          : this.dialogData.transition_ids.full_payment,
      params: {
        ...(this.data.extraParams ?? {}),
        ...(type === 'cancel'
          ? { ...(message && { message }) }
          : {
              claim_uuids: this.dialogData.claim_uuids,
              amount: this.dialogData.amount,
            }),
      },
    };

    this.loading = true;
    try {
      await this.workflowService.runTransition(closeValue);
      this.dialogRef.close(closeValue);
    } catch (error) {
      this.workflowService.showTransitionError(error);
    } finally {
      this.loading = false;
    }
  };
}
